import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import {
  MagnifyingGlass,
  Archive,
  ArrowUUpLeft,
  Dog,
  Cat,
  PawPrint,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { speciesLabel, sexLabel } from "@/lib/patientUtils";
import { formatDate } from "@/lib/dateFmt";

function speciesBadge(sp) {
  const s = (sp || "").toLowerCase();
  const Icon = s === "cat" ? Cat : s === "dog" ? Dog : PawPrint;
  const color = s === "cat" ? "text-pf-accent" : s === "dog" ? "text-pf-primary" : "text-pf-text-secondary";
  return <Icon size={16} weight="fill" className={color} />;
}

const REASON_LABELS = {
  deceased: "archiveReasonDeceased",
  moved: "archiveReasonMoved",
  rejected: "archiveReasonRejected",
  other: "archiveReasonOther",
};

export default function ArchivePage() {
  const { t } = useLang();
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);

  async function load() {
    try {
      const params = { limit: 500, archived_only: true };
      if (q) params.q = q;
      const { data } = await api.get("/patients", { params });
      setRows(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => {
    const h = setTimeout(load, 200);
    return () => clearTimeout(h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  async function unarchive(e, p) {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(t("unarchiveConfirm"))) return;
    try {
      await api.post(`/patients/${p.id}/unarchive`);
      toast.success(t("saved"));
      setRows((rs) => rs.filter((r) => r.id !== p.id));
    } catch (err) {
      toast.error(formatApiError(err));
    }
  }

  return (
    <div className="animate-fade-in-up">
      <div className="flex items-end justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-3xl font-display font-extrabold text-pf-text flex items-center gap-2">
            <Archive size={28} className="text-pf-warning" />
            {t("archiveNav")}
          </h1>
          <p className="text-sm text-pf-text-secondary mt-1">{t("archiveHelp")}</p>
        </div>
      </div>

      <div className="pf-card p-4 mb-4">
        <div className="relative max-w-md">
          <MagnifyingGlass
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-pf-text-secondary pointer-events-none"
          />
          <input
            data-testid="archive-search"
            className="pf-input pl-9"
            placeholder={t("searchPlaceholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="pf-card overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="bg-pf-bg text-left">
            <tr>
              <th className="px-3 py-2 pf-label w-16">ID</th>
              <th className="px-3 py-2 pf-label">{t("patient")}</th>
              <th className="px-3 py-2 pf-label hidden md:table-cell">{t("species")}</th>
              <th className="px-3 py-2 pf-label hidden lg:table-cell">{t("owner")}</th>
              <th className="px-3 py-2 pf-label">{t("archiveReasonLabel")}</th>
              <th className="px-3 py-2 pf-label hidden md:table-cell">{t("archived")}</th>
              <th className="px-3 py-2 pf-label text-right">{t("actions") || " "}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-pf-border">
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-pf-text-secondary">
                  {t("archiveEmpty")}
                </td>
              </tr>
            ) : (
              rows.map((p) => {
                const reason = p.archive_reason ? t(REASON_LABELS[p.archive_reason] || "archiveReasonOther") : "—";
                return (
                  <tr key={p.id} className="hover:bg-pf-bg/60" data-testid={`archive-row-${p.id}`}>
                    <td className="px-3 py-3 font-mono text-xs text-pf-text-secondary">
                      #{p.seq || "—"}
                    </td>
                    <td className="px-3 py-3">
                      <Link to={`/patients/${p.id}`} className="flex items-center gap-2 hover:text-pf-primary">
                        {speciesBadge(p.species)}
                        <div>
                          <div className="font-semibold text-pf-text">{p.name}</div>
                          <div className="text-xs text-pf-text-secondary">
                            {p.breed || "—"} · {sexLabel(p.sex, t)}
                          </div>
                        </div>
                      </Link>
                    </td>
                    <td className="px-3 py-3 hidden md:table-cell">{speciesLabel(p.species, t)}</td>
                    <td className="px-3 py-3 hidden lg:table-cell text-pf-text-secondary">
                      {p.owner?.full_name || "—"}
                    </td>
                    <td className="px-3 py-3">
                      <span className="pf-chip">{reason}</span>
                      {p.archive_note && (
                        <div className="text-xs text-pf-text-secondary mt-1 max-w-xs truncate" title={p.archive_note}>
                          {p.archive_note}
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 hidden md:table-cell text-pf-text-secondary text-xs">
                      {p.archived_at ? formatDate(p.archived_at) : "—"}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={(e) => unarchive(e, p)}
                        className="pf-btn-ghost !py-1 !px-2 text-xs !text-pf-primary !border-pf-primary/30 hover:!bg-pf-primary-light"
                        data-testid={`archive-unarchive-${p.id}`}
                      >
                        <ArrowUUpLeft size={13} /> {t("unarchive")}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
